import dayjs from 'dayjs'
import {DialogDismiss} from '@/components/ui/dialog-atoms/DialogAtoms'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Label} from '@/components/ui/label/Label'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertTriangleIcon} from '@/components/ui/icon/Icon'
import {RadioSteps} from '@/components/commons/radio-steps/RadioSteps'
import {DateRangeFilter} from '@/components/commons/date-range-filter/DateRangeFilter'
import {DateRangeFilterEnum} from '@/types'
import {useTranslation} from '@/translations/i18n'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {
    StyledExportRiaDialogBody,
    StyledExportRiaDisabledSubmitTooltipTarget,
    StyledExportRiaDialogFooter,
    StyledExportRiaErrorBox,
    StyledExportRiaFieldRow
} from './style'

export type ExportRiaPeriod = {
    startDate: string
    endDate: string
    range: DateRangeFilterEnum
}

export type ExportRiaFormat = 'csv' | 'pdf'

const MIN_YEAR = 2000
const MAX_YEAR = 2100

type ExportRiaConfigStepProps = {
    period: ExportRiaPeriod | null
    format: ExportRiaFormat
    onPeriodApply: (period: ExportRiaPeriod) => void
    onFormatChange: (format: ExportRiaFormat) => void
    isSubmitting: boolean
    submitError: string | null
    onSubmit: () => void
}

export const ExportRiaConfigStep = ({
    period,
    format,
    onPeriodApply,
    onFormatChange,
    isSubmitting,
    submitError,
    onSubmit
}: ExportRiaConfigStepProps) => {
    const {t} = useTranslation()

    const formatSteps = (['csv', 'pdf'] as const satisfies readonly ExportRiaFormat[]).map(value => ({
        label: t(value === 'csv' ? 'accesses:export_ria_format_csv' : 'accesses:export_ria_format_pdf'),
        value
    }))

    const year = period ? dayjs(period.startDate).year() : null
    const isYearOutOfRange = year !== null && (year < MIN_YEAR || year > MAX_YEAR)
    const isPdfMonthlyExport = format === 'pdf' && period?.range === DateRangeFilterEnum.Enum.month
    const canSubmit = !!period && !isYearOutOfRange && !isPdfMonthlyExport

    const submitButton = (
        <Button variant="primaryNeutral" onClick={onSubmit} disabled={!canSubmit || isSubmitting}>
            {t('accesses:export_ria_submit')}
            {isSubmitting && <Spinner />}
        </Button>
    )

    return (
        <>
            <StyledExportRiaDialogBody>
                <StyledExportRiaFieldRow>
                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <Label>{t('accesses:export_ria_period_label')}</Label>
                        <DateRangeFilter
                            values={period ?? undefined}
                            onApply={onPeriodApply}
                            visibleOptions={[DateRangeFilterEnum.Enum.month, DateRangeFilterEnum.Enum.year]}
                            placeholder={t('accesses:export_ria_select_period')}
                            portal
                            fullWidth
                        />
                    </Flexbox>

                    <Flexbox direction="column" gap={1.5} fullWidth>
                        <Label>{t('accesses:export_ria_format_label')}</Label>
                        <RadioSteps<ExportRiaFormat>
                            fullWidth
                            itemWidth="100%"
                            size="md"
                            name="export-ria-format"
                            steps={formatSteps}
                            value={format}
                            onChange={onFormatChange}
                        />
                    </Flexbox>
                </StyledExportRiaFieldRow>

                {isYearOutOfRange && (
                    <StyledExportRiaErrorBox>
                        <AlertTriangleIcon size={16} />
                        {t('accesses:export_ria_year_range_error')}
                    </StyledExportRiaErrorBox>
                )}

                {submitError && (
                    <StyledExportRiaErrorBox>
                        <AlertTriangleIcon size={16} />
                        {submitError}
                    </StyledExportRiaErrorBox>
                )}
            </StyledExportRiaDialogBody>
            <StyledExportRiaDialogFooter>
                <DialogDismiss render={<Button variant="secondary">{t('commons:cancel')}</Button>} />
                {isPdfMonthlyExport ? (
                    <Tooltip
                        variant="light"
                        content={t('accesses:export_ria_pdf_year_only_tooltip')}
                        triggerProps={{asChild: true}}
                    >
                        <StyledExportRiaDisabledSubmitTooltipTarget>
                            {submitButton}
                        </StyledExportRiaDisabledSubmitTooltipTarget>
                    </Tooltip>
                ) : (
                    submitButton
                )}
            </StyledExportRiaDialogFooter>
        </>
    )
}
